import Cookies from "js-cookie";
import {
  Award,
  Camera,
  Info,
  Mic,
  Music,
  Sparkles,
  Upload,
  Users,
  Video,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { fetchActiveContests } from "../../../redux/slices/andArenaSlice";
import { notifyError } from "../../../utils";
import {
  DANCER_AND_ARENA,
  MAGICIAN,
  PHOTOGRAPHER_AND_ARENA,
  SINGER_AND_ARENA,
  VIDEO_EDITOR_AND_ARENA,
} from "../../../utils/images";
import SubmitPerformancePage from "../../ANDCompeteNow/SubmitPerformancePage";
import classes from "../participateInAndArenaPages.module.css";

const categoryMapping = {
  singing: {
    title: "SINGING",
    image: SINGER_AND_ARENA,
    quote:
      "The only thing better than singing is more singing. - Ella Fitzgerald",
    icon: <Mic size={18} />,
    contestCategory: "singer",
  },
  photography: {
    title: "PHOTOGRAPHY",
    image: PHOTOGRAPHER_AND_ARENA,
    quote: "Photography is the story I fail to put into words. - Destin Sparks",
    icon: <Camera size={18} />,
    contestCategory: "photographer",
  },
  videoEditing: {
    title: "VIDEO EDITING",
    image: VIDEO_EDITOR_AND_ARENA,
    quote: "Editing is where movies are made or broken. - Edward Dmytryk",
    icon: <Video size={18} />,
    contestCategory: "video_editor",
  },
  dancing: {
    title: "DANCING",
    image: DANCER_AND_ARENA,
    quote: "Dance is the hidden language of the soul. - Martha Graham",
    icon: <Music size={18} />,
    contestCategory: "dancer",
  },
  magic: {
    title: "MAGIC",
    image: MAGICIAN,
    quote:
      "Magic is believing in yourself. If you can do that, you can make anything happen. - Johann Wolfgang von Goethe",
    icon: <Sparkles size={18} />,
    contestCategory: "magician",
  },
};

const ParticipateInCategoryPage = () => {
  const { categoryName } = useParams();
  const [submitPerformanceOpen, setSubmitPerformanceOpen] = useState(false);
  const [currentContest, setCurrentContest] = useState(null);

  const user = Cookies.get("user");
  const userParsed = user ? JSON.parse(user) : null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeContests = useSelector((state) => state.andArena.activeContests);

  // Get the category information or use a default if URL param doesn't match
  const categoryInfo = categoryMapping[categoryName] || categoryMapping.dancing;

  useEffect(() => {
    dispatch(
      fetchActiveContests({
        page_number: 1,
        page_size: 10,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (activeContests && activeContests.length > 0) {
      const matchingContest = activeContests.find(
        (contest) => contest.category === categoryInfo.contestCategory
      );
      setCurrentContest(matchingContest);
    }
  }, [activeContests, categoryInfo.contestCategory]);

  const handleGoBackStageClick = () => {
    navigate("/artist-next-door-arena/compete");
  };

  const handleCompeteNowBtn = () => {
    if (!userParsed) {
      notifyError("Please login to continue");
      return;
    }

    if (userParsed?.type === "artist") {
      setSubmitPerformanceOpen(true);
    } else {
      notifyError("Please Login As Artist To Continue");
    }
  };

  // Get a second quote based on the category
  const getSecondQuote = () => {
    switch (categoryName) {
      case "singing":
        return "Reality is wrong. Dreams are for real. - Tupac Shakur";
      case "photography":
        return "A photograph is a secret about a secret. - Diane Arbus";
      case "videoEditing":
        return "Film editing is the art of making the invisible visible. - Norman Hollyn";
      case "magic":
        return "Magic is the only honest profession. A magician promises to deceive you and he does. - Karl Germain";
      case "dancing":
      default:
        return "Dance first. Think later. It's the natural order. - Samuel Beckett";
    }
  };

  // Format the end date for display
  const getContestEndDate = () => {
    if (!currentContest) return "Loading...";
    return "24/25/25";
  };

  // Generate prizes based on category
  const generatePrizes = () => {
    switch (categoryName) {
      case "singing":
        return (
          <>
            <li>{"1st Place: Amazon Voucher + AND Spotlight (3 Days)"}</li>
            <li>{"2nd Place: Amazon Voucher + AND Spotlight (2 Days)"}</li>
            <li>{"3rd Place: Amazon Voucher + AND Spotlight (1 Day)"}</li>
          </>
        );
      case "photography":
        return (
          <>
            <li>{"1st Place: Amazon Voucher + AND Spotlight (3 Days)"}</li>
            <li>{"2nd Place: Amazon Voucher + AND Spotlight (2 Days)"}</li>
            <li>{"3rd Place: Amazon Voucher + AND Spotlight (1 Day)"}</li>
          </>
        );
      case "videoEditing":
        return (
          <>
            <li>{"1st Place: Amazon Voucher + AND Spotlight (3 Days)"}</li>
            <li>{"2nd Place: Amazon Voucher + AND Spotlight (2 Days)"}</li>
            <li>{"3rd Place: Amazon Voucher + AND Spotlight (1 Day)"}</li>
          </>
        );
      case "magic":
        return (
          <>
            <li>{"1st Place: Amazon Voucher + AND Spotlight (3 Days)"}</li>
            <li>{"2nd Place: Amazon Voucher + AND Spotlight (2 Days)"}</li>
            <li>{"3rd Place: Amazon Voucher + AND Spotlight (1 Day)"}</li>
          </>
        );
      case "dancing":
      default:
        return (
          <>
            <li>{"1st Place: Amazon Voucher + AND Spotlight (3 Days)"}</li>
            <li>{"2nd Place: Amazon Voucher + AND Spotlight (2 Days)"}</li>
            <li>{"3rd Place: Amazon Voucher + AND Spotlight (1 Day)"}</li>
          </>
        );
    }
  };

  return (
    <>
      {submitPerformanceOpen === false ? (
        <div className={classes.container}>
          <ToastContainer />
          <div className={classes.quotesBanner}>
            <p className={classes.quote}>{categoryInfo?.quote}</p>
            <p className={classes.quote}>{getSecondQuote()}</p>
          </div>

          <div className={classes.contestHeader}>
            <div className={classes.leftContent}>
              <h1 className={classes.title}>Live weekly contests</h1>
              <span className={classes.accent}></span>
            </div>
            <div className={classes.statsBox}>
              <p>1000+ artists performing</p>
            </div>
          </div>

          <div className={classes.contentWrapper}>
            <div className={classes.infoSection}>
              <h2 className={classes.sectionTitle}>How It Works?</h2>

              <ol className={classes.rulesList}>
                <li>
                  <strong>Submit Your Performance</strong> - Upload your best
                  {categoryName === "photography"
                    ? " photo"
                    : categoryName === "videoEditing"
                    ? " video edit"
                    : " video/audio clip"}{" "}
                  and enter the contest!
                  <span className={classes.icon}>
                    <Upload size={18} />
                  </span>
                </li>
                <li>
                  <strong>Get Votes & Climb the Leaderboard</strong> - Fans &
                  fellow artists vote for their favorites!
                  <span className={classes.icon}>
                    <Users size={18} />
                  </span>
                </li>
                <li>
                  <strong>
                    The top-voted artist takes home exclusive rewards
                  </strong>
                  , exposure, and bragging rights!
                  <span className={classes.icon}>
                    <Award size={18} />
                  </span>
                </li>
                <li>
                  <strong>Weekly Winners Showcase</strong> - Featured on our
                  homepage and social media channels!
                  <span className={classes.icon}>{categoryInfo?.icon}</span>
                </li>
              </ol>

              <div className={classes.actionButtons}>
                <button
                  className={classes.primaryButton}
                  onClick={handleCompeteNowBtn}
                >
                  COMPETE NOW
                </button>
                <button
                  className={classes.secondaryButton}
                  onClick={handleGoBackStageClick}
                >
                  GO BACKSTAGE
                </button>
              </div>
            </div>

            <div className={classes.visualSection}>
              <div className={classes.microphoneWrapper}>
                <img
                  src={categoryInfo?.image}
                  alt={`${categoryInfo.title}`}
                  className={classes.microphoneImage}
                />
              </div>

              <div className={classes.categorySelector}>
                <h2 className={classes.categoryTitle}>{categoryInfo?.title}</h2>
              </div>
            </div>
          </div>

          <div className={classes.tipSection}>
            <span className={classes.tipIcon}>
              <Info size={18} />
            </span>
            <p className={classes.tipText}>
              Select the category you want to perform in, visit the backstage to
              view artist performances.
            </p>
          </div>

          <div className={classes.footer}>
            <div className={classes.prizesInfo}>
              <h3>This Week's Prizes:</h3>
              <ul>{generatePrizes()}</ul>
            </div>
            <div className={classes.deadlineInfo}>
              <p>
                Current Contest Ends: <strong>{getContestEndDate()}</strong>
              </p>
            </div>
          </div>
        </div>
      ) : (
        <SubmitPerformancePage
          categoryName={categoryInfo?.title}
          contestId={currentContest?.id}
          setSubmitPerformanceOpen={setSubmitPerformanceOpen}
        />
      )}
    </>
  );
};

export default ParticipateInCategoryPage;
